/**
 * One fixture.
 *
 * The id used to arrive as an awaited `params` promise from Next; here it comes
 * off the router. That difference is the whole reason this app left Next: a
 * static export would have needed every fixture id at build time, and a fixture
 * created after the last deploy would 404.
 */

import { useParams } from "react-router-dom";

import { FixtureDetail } from "@/components/fixtures/FixtureDetail";
import { Crumbs, PageHead, Problem } from "@/components/kit";
import { ApiError, getDefinition, getFixture } from "@/lib/api/client";
import type { FieldDef } from "@/lib/api/types";
import { useApi } from "@/spa/useApi";

export function FixtureDetailPage() {
  const { id = "" } = useParams<{ id: string }>();

  const { data, loading, error, cause } = useApi(
    async () => {
      const env = await getFixture(id);
      // The sport's own field-sets, so figures read as "Finish time" rather
      // than "finishTime" and a duration renders as a time rather than a
      // millisecond count. A fixture whose sport has since been unpublished
      // still renders, which is why this one is allowed to fail.
      const sport = env.sportCode as string | undefined;
      const definition = sport ? await getDefinition(sport).catch(() => null) : null;
      return { env, sport, definition };
    },
    [id],
    "failed to load fixture",
  );

  if (error) {
    // "No such fixture" and "the service is down" are different problems and
    // the reader can act on only one of them, so they read differently.
    if (cause instanceof ApiError && cause.status === 404) {
      return <Problem>Fixture not found.</Problem>;
    }
    return <Problem>Could not load fixture: {error}</Problem>;
  }
  if (!data) return <PageHead title="Fixture" note={loading ? "Loading…" : ""} />;

  const { env, sport, definition } = data;
  const formatFields: FieldDef[] = definition?.entities?.["fixture.format"] ?? [];
  const metricFields: FieldDef[] = definition?.entities?.["fixtureCompetitor.metric"] ?? [];

  return (
    <>
      {/* Where it sits, always. An Olympic heat is five levels down, and a
          fixture whose place cannot be seen is one nobody can check. */}
      <Crumbs
        trail={[
          { label: "Sports", href: "/sports" },
          ...(sport ? [{ label: sport, href: `/${sport}` }] : []),
          ...(env.path ?? []).map((step) => ({ label: step.name })),
        ]}
      />
      <FixtureDetail
        env={env}
        formatFields={formatFields}
        metricFields={metricFields}
        icon={definition?.icon}
        fixtureId={id}
      />
    </>
  );
}
